import React, { Component } from "reactn";
import { _ } from "js/vendor";
import db from "js/db";
import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import { AssetType } from "common/constants";
import { builtInFonts } from "common/fontConstants";
import { fontManager } from "js/core/services/fonts";
import Decoration from "./components/Decoration";
import Footer from "./components/Footer";
import ThemeColors from "./components/ThemeColors";
import ThemeNav from "./components/ThemeNav";
import ThemeSimple from "./components/ThemeSimple";
import ThemeTypography from "./components/ThemeTypography";
import styled from "styled-components";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { ChooseThemeDialog } from "js/editor/dialogs/chooseThemeDialog";
import { FeatureType } from "common/features";
import { delay } from "js/core/utilities/promiseHelper";

const ThemeEditorContainer = styled.div`
    background-color: #fff;
    color: #555;
    height: 100%;
    text-transform: uppercase;
`;

export default class ThemeEditor extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selected: "simple",
            theme: null,
            iconBaseUrl: props.icons?.baseUrl,
            fonts: [],
        };
        this.themeFontIds = [];
    }

    async componentDidMount() {
        let {
            theme$
        } = this.props;

        this.subs$ = theme$.subscribe(theme => {
            this.themeFontIds = theme.getFontIds();
            this.setState({ theme: theme.attributes });
        });

        let { iconBaseUrl } = this.state;
        if (!iconBaseUrl) {
            let config = await db("config")
                .once("value")
                .then(snapshot => snapshot.val());
            iconBaseUrl = config.iconBaseUrl;
            this.setState({ iconBaseUrl });
        }
    }

    componentWillUnmount() {
        this.subs$ && this.subs$.unsubscribe();
    }

    updateTheme(deltaTheme) {
        let { updateTheme } = this.props;
        let { theme } = this.state;

        // if we're updating to gradient, and not already set to gradient, save the default background color
        if (
            ["gradient", "reverseGradient"].includes(deltaTheme.styleBackground) &&
            !["gradient", "reverseGradient"].includes(theme.styleBackground)
        ) {
            deltaTheme.lastDefaultBackgroundColor = theme.defaultBackgroundColor;
        } else if (deltaTheme.defaultBackgroundColor) {
            deltaTheme.lastDefaultBackgroundColor = deltaTheme.defaultBackgroundColor;
        }

        updateTheme(deltaTheme);
    }

    fetchFonts = async () => {
        let {
            orgId,
            shareFontsWithParent,
        } = this.props;

        let {
            theme,
        } = this.state;

        const fontAssets = await ds.assets.getAssetsByType(AssetType.FONT, orgId);

        const fontIds = fontAssets.map(({ id }) => id);
        fontIds.push(...Object.keys(builtInFonts));

        let fonts = await Promise.all(fontIds.map(async fontId => fontManager.loadFont(fontId)));

        const addUnloadedFont = async (originalFontId, activeFontId) => {
            if (originalFontId) {
                if (!fonts.some(x => x.id === originalFontId)) {
                    let font = await fontManager.loadFont(activeFontId);
                    font = _.cloneDeep(font);
                    font.id = originalFontId;
                    font.isInvalid = true;
                    fonts.push(font);
                }
            }
        };

        let {
            originalFontHeaderFontId,
            originalFontTitleFontId,
            originalFontBodyFontId,
            fontHeaderFontId,
            fontTitleFontId,
            fontBodyFontId,
        } = theme;

        await addUnloadedFont(originalFontHeaderFontId, fontHeaderFontId);
        await addUnloadedFont(originalFontTitleFontId, fontTitleFontId);
        await addUnloadedFont(originalFontBodyFontId, fontBodyFontId);

        shareFontsWithParent(fonts);
        this.setState({ fonts });
    };

    importTheme = () => {
        let onlyTeamThemes = !app.user.features.isFeatureEnabled(FeatureType.WORKSPACE_CAN_ACCESS_CUSTOM_THEMES, app.appState.workspaceId);

        let {
            fonts,
        } = this.state;

        let hadLoadedFonts = !!fonts?.length;

        ShowDialog(ChooseThemeDialog, {
            title: "Select a Theme",
            hideUserThemes: onlyTeamThemes,
            hideBuiltInThemes: onlyTeamThemes,
            callback: async theme => {
                this.themeFontIds = theme.getFontIds();
                if (hadLoadedFonts) {
                    this.setState({
                        fonts: [],
                    });
                    // Wait a frame
                    await delay(10);
                }

                // Only import fonts and colors
                let {
                    colors,
                    defaultBackgroundColor,
                    defaultSlideColor,
                    fontBodyFontId,
                    fontBodyLetterSpacing,
                    fontBodyLineHeight,
                    fontBodyScaling,
                    fontBodyTextTransform,
                    fontBodyWeight,
                    fontHeaderFontId,
                    fontHeaderLetterSpacing,
                    fontHeaderLineHeight,
                    fontHeaderScaling,
                    fontHeaderTextTransform,
                    fontHeaderWeight,
                    fontScale,
                    fontTitleFontId,
                    fontTitleLetterSpacing,
                    fontTitleLineHeight,
                    fontTitleScaling,
                    fontTitleTextTransform,
                    fontTitleWeight,
                    originalFontBodyFontId,
                    originalFontHeaderFontId,
                    originalFontTitleFontId,
                    palette_name,
                } = theme.attributes;

                let deltaTheme = {
                    colors,
                    defaultBackgroundColor,
                    defaultSlideColor,
                    fontBodyFontId,
                    fontBodyLetterSpacing,
                    fontBodyLineHeight,
                    fontBodyScaling,
                    fontBodyTextTransform,
                    fontBodyWeight,
                    fontHeaderFontId,
                    fontHeaderLetterSpacing,
                    fontHeaderLineHeight,
                    fontHeaderScaling,
                    fontHeaderTextTransform,
                    fontHeaderWeight,
                    fontScale,
                    fontTitleFontId,
                    fontTitleLetterSpacing,
                    fontTitleLineHeight,
                    fontTitleScaling,
                    fontTitleTextTransform,
                    fontTitleWeight,
                    palette_name,
                };

                if (theme.attributes.originalFontBodyFontId) {
                    deltaTheme.originalFontBodyFontId = theme.attributes.originalFontBodyFontId;
                }
                if (theme.attributes.originalFontHeaderFontId) {
                    deltaTheme.originalFontHeaderFontId = theme.attributes.originalFontHeaderFontId;
                }
                if (theme.attributes.originalFontTitleFontId) {
                    deltaTheme.originalFontTitleFontId = theme.attributes.originalFontTitleFontId;
                }

                this.updateTheme(deltaTheme);

                // Wait a frame
                await delay(10);

                await this.fetchFonts();
            }
        });
    };

    render() {
        let {
            selected,
            theme,
            iconBaseUrl,
            fonts,
        } = this.state;

        let childProps = {
            ...this.props,
            theme,
            iconBaseUrl,
            fonts,
            update: deltaThemeAttributues => this.updateTheme(deltaThemeAttributues),
            importTheme: () => this.importTheme(),
            fetchFonts: () => this.fetchFonts(),
        };

        if (!theme) {
            return null;
        }

        return (
            <ThemeEditorContainer>
                <ThemeNav
                    selected={selected}
                    hideOptionsWhenSelected={[
                        "simple",
                    ]}
                >
                    <ThemeSimple
                        {...childProps}
                        navId="simple"
                        navLabel="Simple Editor"
                        navIcon="auto_fix_high"
                        navAlign="bottom"
                    />
                    <ThemeColors
                        {...childProps}
                        navId="colors"
                        navLabel="Colors"
                        navIcon="color_lens"
                    />
                    <ThemeTypography
                        {...childProps}
                        navId="typography"
                        navLabel="Typography"
                        navIcon="format_size"
                    />
                    <Decoration
                        {...childProps}
                        navId="decoration"
                        navLabel="Decoration"
                        navIcon="gradient"
                    />
                    <Footer
                        {...childProps}
                        navId="footer"
                        navLabel="Footer"
                        navIcon="video_label"
                    />
                </ThemeNav>
            </ThemeEditorContainer>
        );
    }
}
